import React from "react";
import { SideNav, type SideNavProps } from "../SideNav/SideNav";
import { TopNav, type TopNavProps } from "../TopNav/TopNav";

/** Layout options for the AppShell */
export type AppShellProps = {
  /** Structured nav for the left rail */
  sideNav: Omit<SideNavProps, "mobileMenuId" | "sidebarCollapseId">;
  /** Optional top bar slots & options */
  topNav?: Pick<
    TopNavProps,
    | "rightContent"
    | "centerContent"
    | "showMobileMenu"
    | "avatar"
    | "showColorModeSwitcher"
    | "toggleTheme"
    | "isDarkThemeEnabled"
    | "colorMode"
    | "actions"
    | "className"
  >;
  /** Main page content */
  children: React.ReactNode;

  /** Layout knobs */
  contentPaddingX?: string; // e.g. "px-4 md:px-6"
  contentPaddingY?: string; // e.g. "py-4 md:py-6"
  showSidebarBorder?: boolean; // default true
  mainClassName?: string; // extra classes on <main>

  /** Optional: Enable JavaScript progressive enhancements */
  enableJsEnhancements?: boolean;
};

/**
 * Isomorphic AppShell component that works as both server and client component
 * Uses CSS-only interactions by default for full SSR compatibility
 * Can be progressively enhanced with JavaScript when enableJsEnhancements is true
 */
export function AppShell({
  sideNav,
  topNav,
  children,
  contentPaddingX,
  contentPaddingY,
  showSidebarBorder = true,
  mainClassName,
  enableJsEnhancements = false,
}: AppShellProps) {
  // Use stable IDs for CSS selectors
  const mobileMenuId = "app-mobile-menu";
  const sidebarCollapseId = "app-sidebar-collapse";

  return (
    <div className="min-h-[100dvh] bg-background text-text-body relative">
      {/* Hidden checkbox for desktop sidebar collapse */}
      <input type="checkbox" id={sidebarCollapseId} className="sr-only peer/sidebar" aria-hidden="true" />

      {/* Skip link for keyboard users */}
      <a
        href="#app-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 z-50 rounded px-3 py-2 bg-background-surface shadow"
      >
        Skip to content
      </a>

      {/* Top navigation - always visible on mobile */}
      <TopNav
        {...topNav}
        mobileMenuId={mobileMenuId}
        className={["sticky top-0 flex-shrink-0 z-30 md:hidden", topNav?.className].filter(Boolean).join(" ")}
      />

      {/* Main layout container - contains sidebar and content */}
      <div className="flex w-full h-[calc(100dvh-60px)] md:h-[100dvh] relative overflow-hidden">
        {/* Hidden checkbox for mobile menu - inside container for peer selectors */}
        <input type="checkbox" id={mobileMenuId} className="sr-only peer/mobile" aria-hidden="true" />

        {/* Mobile menu overlay - fixed to cover entire viewport including TopNav */}
        <label
          htmlFor={mobileMenuId}
          className="fixed inset-0 bg-black/50 z-[55] opacity-0 pointer-events-none peer-checked/mobile:opacity-100 peer-checked/mobile:pointer-events-auto transition-opacity md:hidden"
          aria-label="Close menu"
        />

        {/* Left rail - SideNav with CSS-based responsive behavior */}
        <aside
          id="app-sidebar"
          className={[
            // Base styles
            "bg-background-surface",
            "border-r border-border-default",
            "transition-all duration-300 ease-in-out",
            // Mobile: fixed full-screen overlay
            "fixed md:relative",
            "left-0 top-0 bottom-0",
            "z-[60] md:z-10",
            "w-80 md:w-64",
            "h-[100dvh] md:h-full",
            "-translate-x-full",
            "peer-checked/mobile:translate-x-0",
            // Desktop: always visible
            "md:translate-x-0",
            showSidebarBorder ? "" : "!border-r-0",
            sideNav?.className || "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <SideNav {...sideNav} mobileMenuId={mobileMenuId} sidebarCollapseId={sidebarCollapseId} className="h-full" />
        </aside>

        {/* Right side: TopNav + Main content */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Top navigation - desktop only */}
          <div className="sticky top-0 flex-shrink-0 hidden md:block z-20">
            <TopNav {...topNav} className={topNav?.className} />
          </div>

          {/* Main content - scrollable */}
          <main
            id="app-content"
            className={[
              "flex-1 overflow-y-auto overflow-x-hidden bg-background-body",
              contentPaddingX,
              contentPaddingY,
              mainClassName || "",
            ].join(" ")}
            role="main"
          >
            {children}
          </main>
        </div>
      </div>

      {/* Minimal CSS for checkbox-based interactions */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (min-width: 768px) {
          /* Collapse sidebar width */
          #${sidebarCollapseId}:checked ~ * #app-sidebar {
            width: 4rem !important;
          }
          
          /* Collapse sidebar text */
          #${sidebarCollapseId}:checked ~ * [data-collapse-hide] {
            display: none !important;
          }
          
          /* Center nav items */
          #${sidebarCollapseId}:checked ~ * [data-collapse-center] {
            justify-content: center;
            padding-left: 0;
            padding-right: 0;
            gap: 0;
          }
          
          /* Adjust icon size when collapsed */
          #${sidebarCollapseId}:checked ~ * [data-collapse-icon] {
            width: 1.25rem !important;
            height: 1.25rem !important;
          }
          
          /* Scale SVG icons inside the icon container */
          #${sidebarCollapseId}:checked ~ * [data-collapse-icon] > svg {
            width: 1.25rem !important;
            height: 1.25rem !important;
          }
          
          /* Rotate toggle icon */
          #${sidebarCollapseId}:checked ~ * [data-collapse-toggle] {
            transform: scaleX(-1);
          }
          
          /* Hide category expansions */
          #${sidebarCollapseId}:checked ~ * [data-collapse-hide-category] {
            display: none;
          }
        }
      `,
        }}
      />

      {/* Optional: Progressive enhancement script */}
      {enableJsEnhancements && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                // Close mobile menu on Escape key
                document.addEventListener('keydown', function(e) {
                  if (e.key === 'Escape') {
                    var menuToggle = document.getElementById('${mobileMenuId}');
                    if (menuToggle) menuToggle.checked = false;
                  }
                });
                
                // Save sidebar collapse preference to localStorage
                var sidebarToggle = document.getElementById('${sidebarCollapseId}');
                if (sidebarToggle) {
                  // Restore from localStorage
                  var saved = localStorage.getItem('sidebar-collapsed');
                  if (saved !== null) {
                    sidebarToggle.checked = saved === 'true';
                  }
                  
                  // Save on change
                  sidebarToggle.addEventListener('change', function() {
                    localStorage.setItem('sidebar-collapsed', this.checked);
                  });
                }
                
                // Prevent body scroll when mobile menu is open
                var mobileToggle = document.getElementById('${mobileMenuId}');
                if (mobileToggle) {
                  mobileToggle.addEventListener('change', function() {
                    document.body.style.overflow = this.checked ? 'hidden' : '';
                  });
                }
                
                // Close mobile menu when clicking navigation links
                var sidebar = document.getElementById('app-sidebar');
                if (sidebar && mobileToggle) {
                  sidebar.addEventListener('click', function(e) {
                    // Check if click target is a link (or inside a link)
                    var link = e.target.closest('a');
                    if (link && link.href && window.innerWidth < 768) {
                      // Small delay to allow navigation to start
                      setTimeout(function() {
                        mobileToggle.checked = false;
                        document.body.style.overflow = '';
                      }, 100);
                    }
                  });
                }
              })();
            `,
          }}
        />
      )}
    </div>
  );
}
